import {Button, Dialog, DialogFooter} from 'platform/components';
import {HStack, useDevice} from 'platform/foundation';

import {useMemo} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../hooks/useCatalogue';
import i18n from '../../../../i18n/i18n';
import {FeaturesCheckboxTree} from '../../../FeaturesCheckboxTree/FeaturesCheckboxTree';

interface FeaturesFilterDialogProps extends TestIdProps {
  isOpen: boolean;
  draftValue: string[];
  isLoading?: boolean;
  onDraftChange: (value: string[]) => void;
  onClose: () => void;
  onConfirm: (value: string[]) => void;
}

export function FeaturesFilterDialog(props: FeaturesFilterDialogProps) {
  const {features, hasFeatureTranslation} = useCatalogue();
  const device = useDevice();
  const isSmallScreen = device === 'mobile' || device === 'tablet';
  const modalSize = isSmallScreen ? 'xLarge' : undefined;

  const translatableFeatures = useMemo(
    () => features?.filter((feature) => hasFeatureTranslation(feature.const_key)) ?? [],
    [features, hasFeatureTranslation]
  );

  return (
    <Dialog
      title={i18n.t('entity.filterForm.featuresDialog.title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      size={modalSize}
      withAdditionalFooter
      scrollBehavior="inside"
      data-testid={suffixTestId('dialog', props)}
    >
      <FeaturesCheckboxTree
        value={props.draftValue}
        onChange={props.onDraftChange}
        features={translatableFeatures}
        isDisabled={props.isLoading}
        data-testid={suffixTestId('tree', props)}
      />
      <DialogFooter data-testid={suffixTestId('footer', props)}>
        <HStack spacing={2} justify="flex-end" grow={1}>
          <Button
            title={i18n.t('entity.filterForm.savedFilters.dialog.cancel')}
            variant="secondary"
            type="button"
            onClick={props.onClose}
            isDisabled={props.isLoading}
            data-testid={suffixTestId('cancelButton', props)}
          />
          <Button
            title={i18n.t('general.actions.confirm')}
            type="button"
            onClick={() => props.onConfirm(props.draftValue)}
            isDisabled={props.isLoading}
            data-testid={suffixTestId('confirmButton', props)}
          />
        </HStack>
      </DialogFooter>
    </Dialog>
  );
}
